import React from 'react';
import { CallRecordKey } from 'flow/types/EntityKeys';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    selectCallRecordIsPersonalRecording,
    selectCallRecordRecordingDownloadUrl,
    selectCallRecordVoiceMessageUniqueId,
    selectExtendedTranscriptionIsPersonal,
} from 'flow/selectors/call-records-selectors';
import ContextMenu from 'flow/components/ContextMenu';
import Icon from 'flow/components/ui-components/Icon';
import useAppContext from 'flow/hooks/useAppContext';
import MenuItemDeleteExtendedTranscription from './MenuItemDeleteExtendedTranscription';
import MenuItemDeleteRecording from './MenuItemDeleteRecording';
import MenuItemDeleteVoicemail from './MenuItemDeleteVoicemail';
import MenuItemDownloadRecording from './MenuItemDownloadRecording';
import MenuItemDownloadVoicemail from './MenuItemDownloadVoicemail';

interface Props {
    callRecordKey: CallRecordKey;
    // Per-side privacy gates from the parent. When true, the corresponding
    // items (download/delete recording, delete transcription) are suppressed
    // so the menu doesn't expose actions on a colleague's personal content.
    recordingIsHidden?: boolean;
    transcriptionIsHidden?: boolean;
    onClickDownloadRecording?: () => void;
}

const RecordingContextMenu = ({
    callRecordKey,
    recordingIsHidden,
    transcriptionIsHidden,
    onClickDownloadRecording,
}: Props) => {
    const { i18next } = useAppContext();
    const hasDownloadRecording = useAppSelector((state) =>
        selectCallRecordRecordingDownloadUrl(state, callRecordKey),
    );
    const hasDeleteRecording = useAppSelector((state) =>
        selectCallRecordIsPersonalRecording(state, callRecordKey),
    );
    const hasDeleteTranscription = useAppSelector((state) =>
        selectExtendedTranscriptionIsPersonal(state, callRecordKey),
    );
    const hasVoicemail = useAppSelector((state) =>
        selectCallRecordVoiceMessageUniqueId(state, callRecordKey),
    );

    const hasAnyItem =
        (!recordingIsHidden && (hasDownloadRecording || hasDeleteRecording)) ||
        (!transcriptionIsHidden && hasDeleteTranscription) ||
        hasVoicemail;

    if (!hasAnyItem) return null;

    return (
        <ContextMenu
            triggerLabel={i18next.t('menu.more_options')}
            className="flex size-10 items-center justify-center bg-gray-200! p-0!"
            preventOverflow={true}
            icon={
                <Icon
                    iconKey="more_vert"
                    size="1.5rem"
                    className="text-gray-600"
                />
            }
        >
            {!recordingIsHidden && (
                <>
                    <MenuItemDownloadRecording
                        callRecordKey={callRecordKey}
                        onClickDownloadRecording={onClickDownloadRecording}
                    />
                    <MenuItemDeleteRecording callRecordKey={callRecordKey} />
                </>
            )}
            {!transcriptionIsHidden && (
                <MenuItemDeleteExtendedTranscription
                    callRecordKey={callRecordKey}
                />
            )}
            <MenuItemDownloadVoicemail callRecordKey={callRecordKey} />
            <MenuItemDeleteVoicemail callRecordKey={callRecordKey} />
        </ContextMenu>
    );
};

export default RecordingContextMenu;
